
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, BookOpen, Clock, TrendingUp, Edit } from 'lucide-react';
import { useNodes } from '@/hooks/useNodes';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import AdvancedNodeEditor from '@/components/editor/AdvancedNodeEditor';
import NodeCard from '@/components/content/NodeCard';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;

const AuthorDashboard: React.FC = () => {
  const { user } = useAuth();
  const { canCreateContent } = useRoleAccess();
  const { data: allNodes } = useNodes();
  const [showEditor, setShowEditor] = useState(false);
  const [editingNode, setEditingNode] = useState<Node | undefined>();

  // Filter nodes by current user
  const myNodes = allNodes?.filter(node => node.author_id === user?.id) || [];
  const draftNodes = myNodes.filter(node => node.status === 'draft');
  const reviewNodes = myNodes.filter(node => node.status === 'in_review');
  const publishedNodes = myNodes.filter(node => node.status === 'published');

  const stats = [
    {
      title: 'Total Content',
      value: myNodes.length,
      icon: BookOpen,
      color: 'text-black',
    },
    {
      title: 'Published',
      value: publishedNodes.length,
      icon: TrendingUp,
      color: 'text-green-600',
    },
    {
      title: 'In Review',
      value: reviewNodes.length,
      icon: Clock,
      color: 'text-black',
    },
    {
      title: 'Drafts',
      value: draftNodes.length,
      icon: Edit,
      color: 'text-black',
    },
  ];

  const handleCreateNew = () => {
    setEditingNode(undefined);
    setShowEditor(true);
  };

  const handleEditNode = (node: Node) => {
    setEditingNode(node);
    setShowEditor(true);
  };

  const handleSaveComplete = () => {
    setShowEditor(false);
    setEditingNode(undefined);
  };

  if (!canCreateContent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-black">
            You need contributor access or higher to use the author dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (showEditor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <AdvancedNodeEditor
          node={editingNode}
          onSave={handleSaveComplete}
          onCancel={() => setShowEditor(false)}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Author Dashboard</h1>
          <p className="text-black mt-1">
            Manage your content and build knowledge connections
          </p>
        </div>
        
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Create Content
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-black">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="drafts" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="drafts">Drafts ({draftNodes.length})</TabsTrigger>
          <TabsTrigger value="review">In Review ({reviewNodes.length})</TabsTrigger>
          <TabsTrigger value="published">Published ({publishedNodes.length})</TabsTrigger>
          <TabsTrigger value="all">All Content ({myNodes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="drafts" className="space-y-4">
          {draftNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <Button
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleEditNode(node)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black mb-4">No draft content</p>
              <Button onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Draft
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          {reviewNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <Badge className="absolute top-2 right-2" variant="secondary">
                    In Review
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black">No content in review</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="published" className="space-y-4">
          {publishedNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <Badge className="absolute top-2 right-2" variant="default">
                    Published
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black">No published content yet</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {myNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Badge variant={
                      node.status === 'published' ? 'default' :
                      node.status === 'in_review' ? 'secondary' :
                      'outline'
                    }>
                      {node.status}
                    </Badge>
                    {node.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleEditNode(node)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black mb-4">
                You haven't created any content yet
              </p>
              <Button onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Content
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthorDashboard;
